import ExcelJS from 'exceljs'
import {
  detectLighthouseSheet,
  parseLighthouseSheet
} from 'features/RateCodes/lighthouseFormat'
import {
  rateCodeCategoryKeyByLabel,
  rateCodeCategoryNames
} from 'features/RateCodes/rateCodeCategories'

// Enum: key → Excel label
export const CATEGORY_LABELS = rateCodeCategoryNames

// Reverse lookup: Excel label → enum key
const LABEL_TO_KEY = rateCodeCategoryKeyByLabel

export function rateCodesMatch(a, b) {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

/**
 * Merges imported rate codes into UI state.
 * Matches booking rate codes case-insensitively so Excel casing variants still categorize.
 */
export function mergeImportedRateCodes(rateState, importedRateCodes, isSubsidiaryLevel) {
  const updatedRateState = { ...rateState }
  const newChanges = []

  Object.keys(importedRateCodes).forEach(category => {
    if (category === 'Unknown') {
      return
    }

    const ratesToAdd = []

    // Built once per category: updatedRateState only changes between categories, and
    // rebuilding it per row makes large imports quadratic.
    const allCategorized = Object.entries(updatedRateState)
      .filter(([key]) => key !== 'Unknown')
      .flatMap(([, rates]) => rates || [])

    importedRateCodes[category].forEach(rate => {
      const matchingUnknown = (updatedRateState.Unknown || []).filter(unknownRate =>
        rateCodesMatch(unknownRate.code, rate.code)
      )

      if (matchingUnknown.length > 0) {
        matchingUnknown.forEach(unknownRate => {
          if (!ratesToAdd.some(r => r.code === unknownRate.code)) {
            ratesToAdd.push({
              ...unknownRate,
              isImported: true
            })
          }
        })
        return
      }

      if (!allCategorized.some(existingRate => rateCodesMatch(existingRate.code, rate.code))) {
        ratesToAdd.push({
          ...rate,
          isImported: true
        })
      }
    })

    if (ratesToAdd.length === 0) {
      return
    }

    const existingCategory = updatedRateState[category] || []
    const mergedCategory = [...existingCategory]

    ratesToAdd.forEach(newRate => {
      const existingIndex = mergedCategory.findIndex(
        existingRate => existingRate.code === newRate.code
      )

      if (existingIndex >= 0) {
        mergedCategory[existingIndex] = newRate
      } else {
        mergedCategory.push(newRate)
      }
    })

    updatedRateState[category] = mergedCategory

    if (updatedRateState.Unknown) {
      updatedRateState.Unknown = updatedRateState.Unknown.filter(
        unknownRate => !ratesToAdd.some(r => rateCodesMatch(r.code, unknownRate.code))
      )
    }

    ratesToAdd.forEach(rate => {
      newChanges.push({
        isSubsidiaryLevel,
        code: rate.code,
        kind: category
      })
    })
  })

  return { updatedRateState, newChanges }
}

const ACCEPTED_EXTENSION = '.xlsx'

function validateFileName(fileName) {
  if (!fileName.toLowerCase().endsWith(ACCEPTED_EXTENSION)) {
    // ExcelJS cannot read the legacy binary .xls format, so accepting it here only defers
    // the failure to an unreadable parse error.
    return `Invalid file type. Expected an Excel ${ACCEPTED_EXTENSION} file, got: ${fileName}. Legacy .xls files must be saved as ${ACCEPTED_EXTENSION} first.`
  }

  return null
}

async function readWorkbook(file) {
  const arrayBuffer = await file.arrayBuffer()
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(arrayBuffer)
  return workbook
}

function resolveNativeSheet(workbook) {
  return workbook.getWorksheet('Rates') || workbook.worksheets[0] || null
}

function validateNativeSheet(workbook, sheet) {
  const errors = []
  const warnings = []
  let rowCount = 0

  // Check if 'Rates' sheet exists (warning if using fallback)
  if (!workbook.getWorksheet('Rates')) {
    warnings.push(`Sheet named "Rates" not found. Using first sheet: "${sheet.name}"`)
  }

  // Validate header row
  const headerRow = sheet.getRow(1)
  const codeHeader = headerRow.getCell(1).value
  const categoryHeader = headerRow.getCell(2).value

  if (!codeHeader && !categoryHeader) {
    errors.push('Header row appears to be empty. Expected columns: Rate code, Category')
  }

  // Validate data rows
  const invalidCategories = new Set()
  const emptyCodeRows = []

  sheet.eachRow((row, rowNo) => {
    if (rowNo === 1) return // skip header

    rowCount++

    const codeValue = row.getCell(1).value
    const categoryValue = row.getCell(2).value

    // Check for empty code
    if (!codeValue || String(codeValue).trim() === '') {
      emptyCodeRows.push(rowNo)
    }

    // Validate category value
    if (categoryValue) {
      const categoryLabel = String(categoryValue).trim()
      const isValidCategory = Object.values(CATEGORY_LABELS).includes(categoryLabel)

      if (!isValidCategory) {
        invalidCategories.add(categoryLabel)
      }
    }
  })

  // Report empty code rows
  if (emptyCodeRows.length > 0) {
    if (emptyCodeRows.length <= 5) {
      warnings.push(`Empty code values found in rows: ${emptyCodeRows.join(', ')}`)
    } else {
      warnings.push(
        `Empty code values found in ${
          emptyCodeRows.length
        } rows (first 5: ${emptyCodeRows.slice(0, 5).join(', ')}...)`
      )
    }
  }

  // Report invalid categories as warnings (not errors) - they will be filtered out
  if (invalidCategories.size > 0) {
    const invalidList = Array.from(invalidCategories).slice(0, 5).join(', ')
    const suffix = invalidCategories.size > 5 ? '...' : ''
    warnings.push(
      `Unknown category values found: ${invalidList}${suffix}. These rows will be available for download.`
    )
  }

  // Check for minimum data
  if (rowCount === 0) {
    errors.push('Excel file contains no data rows (only header found)')
  }

  return { errors, warnings, rowCount }
}

/**
 * Validates an Excel file for rate code import.
 * Checks file structure, required columns, and category values.
 *
 * @param {File} file - The Excel file to validate
 * @returns {Promise<{valid: boolean, errors: string[], warnings: string[], rowCount: number}>}
 */
export async function validateExcelFile(file) {
  const errors = []
  const warnings = []
  let rowCount = 0

  // Check file extension
  const fileName = file.name || ''
  const extensionError = validateFileName(fileName)

  if (extensionError) {
    errors.push(extensionError)
    return { valid: false, errors, warnings, rowCount }
  }

  try {
    const workbook = await readWorkbook(file)

    // Check for worksheets
    if (!workbook.worksheets || workbook.worksheets.length === 0) {
      errors.push('Excel file contains no worksheets')
      return { valid: false, errors, warnings, rowCount }
    }

    const sheet = resolveNativeSheet(workbook)

    if (!sheet) {
      errors.push('Could not find any sheet in Excel file')
      return { valid: false, errors, warnings, rowCount }
    }

    const validation = validateNativeSheet(workbook, sheet)

    errors.push(...validation.errors)
    warnings.push(...validation.warnings)
    rowCount = validation.rowCount
  } catch (error) {
    errors.push(`Failed to parse Excel file: ${error.message}`)
    return { valid: false, errors, warnings, rowCount }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    rowCount
  }
}

function parseNativeSheet(sheet) {
  // Initialize empty buckets for all categories (so missing ones remain like [])
  const result = {}
  const unknownRows = []

  // eslint-disable-next-line no-unused-vars
  const { Unknown, ...categoryLabelsExceptUnknown } = CATEGORY_LABELS

  for (const key of Object.keys(categoryLabelsExceptUnknown)) {
    result[key] = []
  }

  // Last row wins for duplicate codes (case-insensitive), matching backend save behavior.
  const codeAssignments = new Map()

  sheet.eachRow((row, rowNo) => {
    if (rowNo === 1) return // skip header

    const codeValue = row.getCell(1).value
    const categoryValue = row.getCell(2).value

    if (categoryValue === CATEGORY_LABELS.Unknown) return // skip Uncategorized rows

    if (!codeValue) return

    const code = String(codeValue).trim()
    const categoryLabel = categoryValue ? String(categoryValue).trim() : ''

    // map label to enum key
    const bucketKey = LABEL_TO_KEY[categoryLabel]

    if (!bucketKey) {
      // Unknown category - collect for CSV download
      unknownRows.push({
        code,
        category: categoryLabel || '(empty)',
        rowNumber: rowNo
      })
      return
    }

    codeAssignments.set(code.toLowerCase(), { bucketKey, code })
  })

  codeAssignments.forEach(({ bucketKey, code }) => {
    result[bucketKey].push({ code })
  })

  return { buckets: result, unknownRows }
}

/**
 * Reads uploaded Excel file and returns grouped bucket JSON.
 * Output:
 * {
 *   buckets: { OTA: [{code}], ... },
 *   unknownRows: [{ code, category, rowNumber }]
 * }
 */
export async function parseExcelToRateCodeBuckets(file) {
  const workbook = await readWorkbook(file)

  const sheet = resolveNativeSheet(workbook)

  if (!sheet) throw new Error('Could not find any sheet in Excel')

  return parseNativeSheet(sheet)
}

function escapeCsvValue(value) {
  return `"${String(value === null || value === undefined ? '' : value).replace(
    /"/g,
    '""'
  )}"`
}

/**
 * Generates a CSV string from reported rows
 * @param {Array<{code: string, category: string, description?: string, rowNumber: number}>} unknownRows
 * @returns {string} CSV content
 */
export function generateUnknownRowsCsv(unknownRows) {
  const header = 'Code,Description,Category,Original Row Number'
  const rows = unknownRows.map(
    row =>
      `${escapeCsvValue(row.code)},${escapeCsvValue(row.description)},${escapeCsvValue(
        row.category
      )},${row.rowNumber}`
  )
  return [header, ...rows].join('\n')
}

/**
 * Creates a downloadable CSV blob URL from unknown rows
 * @param {Array<{code: string, category: string, rowNumber: number}>} unknownRows
 * @returns {string} Blob URL for download
 */
export function createUnknownRowsCsvDownloadUrl(unknownRows) {
  const csvContent = generateUnknownRowsCsv(unknownRows)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  return URL.createObjectURL(blob)
}

/**
 * Groups reported rows by their source category, most frequent first.
 * @returns {Array<{category: string, count: number}>}
 */
export function summarizeRowsByCategory(rows) {
  const countsByCategory = new Map()

  ;(rows || []).forEach(row => {
    const category = row.category || '(empty)'
    countsByCategory.set(category, (countsByCategory.get(category) || 0) + 1)
  })

  return Array.from(countsByCategory.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count)
}

function describeSummary(rows, maxEntries = 5) {
  const summary = summarizeRowsByCategory(rows)
  const listed = summary
    .slice(0, maxEntries)
    .map(entry => `${entry.count} ${entry.category}`)
    .join(', ')
  const suffix = summary.length > maxEntries ? ', ...' : ''

  return `${listed}${suffix}`
}

function emptyParseResult(format, errors) {
  return {
    format,
    valid: false,
    errors,
    warnings: [],
    rowCount: 0,
    buckets: {},
    unknownRows: [],
    uncategorizedRows: [],
    hotelNameFromFile: ''
  }
}

/**
 * Single entry point for the import dialog. Reads the workbook once, decides whether it is
 * our native export or a Lighthouse mapping file, and returns one shape for both.
 *
 * @param {File} file
 * @returns {Promise<{
 *   format: 'native'|'lighthouse',
 *   valid: boolean,
 *   errors: string[],
 *   warnings: string[],
 *   rowCount: number,
 *   buckets: Object,
 *   unknownRows: Array,
 *   uncategorizedRows: Array,
 *   hotelNameFromFile: string
 * }>}
 */
export async function parseRateCodeWorkbook(file) {
  const fileName = file.name || ''
  const extensionError = validateFileName(fileName)

  if (extensionError) {
    return emptyParseResult('native', [extensionError])
  }

  let workbook

  try {
    workbook = await readWorkbook(file)
  } catch (error) {
    return emptyParseResult('native', [`Failed to parse Excel file: ${error.message}`])
  }

  if (!workbook.worksheets || workbook.worksheets.length === 0) {
    return emptyParseResult('native', ['Excel file contains no worksheets'])
  }

  const lighthouseSheet = detectLighthouseSheet(workbook)

  if (lighthouseSheet) {
    return parseLighthouseWorkbook(lighthouseSheet)
  }

  return parseNativeWorkbook(workbook)
}

function parseLighthouseWorkbook(lighthouseSheet) {
  const errors = []
  const warnings = []

  let parsed

  try {
    parsed = parseLighthouseSheet(lighthouseSheet)
  } catch (error) {
    return emptyParseResult('lighthouse', [
      `Failed to read the Lighthouse mapping sheet: ${error.message}`
    ])
  }

  if (parsed.rowCount === 0) {
    errors.push('Lighthouse mapping file contains no rate code rows')
  }

  if (parsed.unknownRows.length > 0) {
    warnings.push(
      `Unrecognized Lighthouse mapping values found (${describeSummary(
        parsed.unknownRows
      )}). Those rows were not imported and are available for download.`
    )
  }

  if (parsed.uncategorizedRows.length > 0) {
    warnings.push(
      `${
        parsed.uncategorizedRows.length
      } row(s) need a manual decision and stay uncategorized: ${describeSummary(
        parsed.uncategorizedRows
      )}.`
    )
  }

  return {
    format: 'lighthouse',
    valid: errors.length === 0,
    errors,
    warnings,
    rowCount: parsed.rowCount,
    buckets: parsed.buckets,
    unknownRows: parsed.unknownRows,
    uncategorizedRows: parsed.uncategorizedRows,
    hotelNameFromFile: parsed.hotelNameFromFile
  }
}

function parseNativeWorkbook(workbook) {
  const sheet = resolveNativeSheet(workbook)

  if (!sheet) {
    return emptyParseResult('native', ['Could not find any sheet in Excel file'])
  }

  const validation = validateNativeSheet(workbook, sheet)

  if (validation.errors.length > 0) {
    return {
      ...emptyParseResult('native', validation.errors),
      warnings: validation.warnings,
      rowCount: validation.rowCount
    }
  }

  const parsed = parseNativeSheet(sheet)

  return {
    format: 'native',
    valid: true,
    errors: [],
    warnings: validation.warnings,
    rowCount: validation.rowCount,
    buckets: parsed.buckets,
    unknownRows: parsed.unknownRows,
    uncategorizedRows: [],
    hotelNameFromFile: ''
  }
}

export default parseExcelToRateCodeBuckets
